import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { GrowthbookFlagsEnum } from 'growthbook/types'
import React from 'react'
import { useHideItem } from 'shared/components/header/hooks/use-hide-item'
import {
  MenuItemInterface,
  SingleItemDropdownInterface,
  SingleItemHrefInterface,
} from 'shared/components/header/types/menu-interface'
import { AdminRolesEnum, UserRolesEnum, UserStatusEnum } from 'shared/enums/user-roles-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import {
  AutomationsIcon,
  BlogsIcon,
  ContactsIcon,
  DashboardIcon,
  EmailsIcon,
  HelpIcon,
  ManInSuit,
  ProductsIcon,
  SalesIcon,
  UpgradeIcon,
  SmsMenuIcon,
} from '@systemeio/ui/icons'
import { formUrl } from 'shared/utils/links/form-url'
import { isClient, isSioHost } from '@systemeio/util'

export const useMenuItems = () => {
  const { t } = useLocoTranslation()
  const websiteFeatureIsOn = useFeatureIsOn(GrowthbookFlagsEnum.Website)
  const whiteLabelFeatureIsOn = useFeatureIsOn(GrowthbookFlagsEnum.WhiteLabel)
  const needToHide = useHideItem()

  const helpItem: SingleItemHrefInterface = {
    name: t('header.top_menu.help'),
    href: `${process.env.NEXT_PUBLIC_SIO_HOST_SCHEME}://${process.env.NEXT_PUBLIC_SIO_HOST_NAME}/help`,
    target: '__blank',
    mobileIconRenderer: () => <HelpIcon />,

    hide: isClient() ? !isSioHost(window.location.host) : true,
  }

  const upgradeAccountItem: SingleItemHrefInterface = {
    href: '/profile/user-plan',

    mobileIconRenderer: () => <UpgradeIcon />,
    name: t('header.top_menu.upgrade'),
  }

  const templateItems: MenuItemInterface['item'][] = [
    {
      name: t('header.top_menu.admin.email_templates'),
      href: '/management/email-templates',
      adminRoles: [AdminRolesEnum.contentManager, AdminRolesEnum.admin, AdminRolesEnum.superAdmin],
    },
    {
      name: t('header.top_menu.admin.page_templates'),
      href: '/management/page-templates',
      adminRoles: [AdminRolesEnum.contentManager, AdminRolesEnum.admin, AdminRolesEnum.superAdmin],
    },
    {
      name: t('header.top_menu.admin.blog_templates'),
      href: '/management/blog-templates',
      adminRoles: [AdminRolesEnum.contentManager, AdminRolesEnum.admin, AdminRolesEnum.superAdmin],
    },
    {
      name: t('header.top_menu.admin.website_templates'),
      href: '/management/website-templates',
      adminRoles: [AdminRolesEnum.contentManager, AdminRolesEnum.admin, AdminRolesEnum.superAdmin],
      hide: !websiteFeatureIsOn,
    },
    {
      name: t('header.top_menu.admin.funnel_templates'),
      href: '/management/funnel-templates',

      adminRoles: [AdminRolesEnum.contentManager, AdminRolesEnum.admin, AdminRolesEnum.superAdmin],
    },
  ]
  const menuItemsAffiliate: MenuItemInterface[] = [
    {
      item: {
        name: t('header.top_menu.dashboard'),
        dropdownItems: [
          {
            name: t('header.top_menu.affiliate_dashboard'),
            href: '/affiliate-dashboard',

            hideForWorkspace: true,
            showForRoles: [UserRolesEnum.affiliate],
          },
          {
            name: t('header.top_menu.student_dashboard'),
            href: formUrl(`/school?tab=explore_courses`, false) ?? '',
            hideForRoles: [UserRolesEnum.student],
          },
        ],
        mobileIconRenderer: () => <DashboardIcon />,
      },
    },
    {
      item: {
        name: t('header.top_menu.admin.admin'),
        isAdmin: true,
        dropdownItems: templateItems,
        mobileIconRenderer: () => <ManInSuit />,
      },
    },
    {
      item: helpItem,
    },
  ]

  const menuItemsAffiliateStudent: MenuItemInterface[] = [
    {
      item: {
        name: t('header.top_menu.dashboard'),
        dropdownItems: [
          {
            name: t('header.top_menu.affiliate_dashboard'),
            href: '/affiliate-dashboard',

            hideForWorkspace: true,
            showForRoles: [UserRolesEnum.affiliate],
          },
          {
            name: t('header.top_menu.student_dashboard'),
            showForRoles: [UserRolesEnum.student],
            href: formUrl('/school', false) ?? '',
          },
          {
            name: t('header.top_menu.student_dashboard'),
            href: formUrl('/school?tab=explore_courses', false) ?? '',
            hideForRoles: [UserRolesEnum.student],
          },
        ],
        mobileIconRenderer: () => <DashboardIcon />,
      },
    },
    {
      item: {
        name: t('header.top_menu.admin.admin'),
        isAdmin: true,
        dropdownItems: templateItems,
        mobileIconRenderer: () => <ManInSuit />,
      },
    },
    {
      item: helpItem,
    },
  ]

  const menuItems: MenuItemInterface[] = [
    {
      item: {
        name: t('header.top_menu.dashboard'),
        dropdownItems: [
          {
            name: t('header.top_menu.main_dashboard'),
            href: '/',
            hideForWorkspaceAssistant: true,
          },
          {
            name: t('header.top_menu.affiliate_dashboard'),
            href: '/affiliate-dashboard',

            hideForWorkspace: true,
            showForRoles: [UserRolesEnum.affiliate],
          },
          {
            name: t('header.top_menu.student_dashboard'),
            showForRoles: [UserRolesEnum.student],
            href: formUrl('/school', false) ?? '',
          },
          {
            name: t('header.top_menu.student_dashboard'),
            href: formUrl('/school?tab=explore_courses', false) ?? '',
            hideForRoles: [UserRolesEnum.student],
          },
          {
            name: t('header.top_menu.agency_dashboard'),
            href: '/agency-center/white-label-users',

            hide: isClient() ? !whiteLabelFeatureIsOn || !isSioHost(window.location.host) : true,
          },
        ],
        mobileIconRenderer: () => <DashboardIcon />,
      },
    },
    {
      item: {
        name: t('header.top_menu.crm'),
        dropdownItems: [
          {
            name: t('header.top_menu.contacts'),
            href: '/contacts',
            assistantLink: '/assistant/contacts',
          },
          { name: t('header.top_menu.tags'), href: '/tags' },
          {
            name: t('header.top_menu.crm_pipeline'),
            href: '/crm-pipeline',
          },
          { name: t('header.top_menu.calendar'), href: '/calendar' },
        ],
        mobileIconRenderer: () => <ContactsIcon />,
      },
    },
    {
      item: {
        name: t('header.top_menu.sites'),
        dropdownItems: [
          {
            name: t('header.top_menu.website'),
            href: '/websites',
            hide: !websiteFeatureIsOn,
          },
          {
            name: t('header.top_menu.funnels'),
            href: '/funnels',
          },
          {
            name: t('header.top_menu.digital_stores'),
            href: '/stores',
          },
          {
            name: t('header.top_menu.blogs'),
            href: '/blogs',
          },
        ],
        mobileIconRenderer: () => <BlogsIcon />,
      },
    },
    {
      item: {
        name: t('header.top_menu.emails'),
        dropdownItems: [
          { name: t('header.top_menu.newsletters'), href: '/newsletters' },
          { name: t('header.top_menu.campaigns'), href: '/campaigns' },
          {
            name: t('header.top_menu.email_statistics'),
            href: '/email-statistics',
          },
        ],
        mobileIconRenderer: () => <EmailsIcon />,
      },
    },
    {
      item: {
        name: t('header.top_menu.sms'),
        dropdownItems: [
          { name: t('header.top_menu.sms_templates'), href: '/sms/templates' },
          { name: t('header.top_menu.sms_statistics'), href: '/sms/statistics' },
        ],
        mobileIconRenderer: () => <SmsMenuIcon />,
      },
    },
    {
      item: {
        name: t('header.top_menu.automations'),
        dropdownItems: [
          { name: t('header.top_menu.rules'), href: '/automation-rules' },
          { name: t('header.top_menu.workflows'), href: '/workflows' },
        ],
        mobileIconRenderer: () => <AutomationsIcon />,
      },
    },
    {
      item: {
        name: t('header.top_menu.products'),
        dropdownItems: [
          {
            name: t('header.top_menu.physical_product'),
            href: '/products',
          },
          { name: t('header.top_menu.coupons'), href: '/coupons' },
          {
            name: t('header.top_menu.courses'),
            href: '/courses',
          },
          {
            name: t('header.top_menu.communities'),
            href: '/communities',
          },
          {
            name: t('header.top_menu.files'),
            href: '/files',
          },
        ],
        mobileIconRenderer: () => <ProductsIcon />,
      },
    },
    {
      item: {
        name: t('header.top_menu.sales'),
        dropdownItems: [
          {
            name: t('header.top_menu.orders'),
            href: '/orders',
          },
          {
            name: t('header.top_menu.transactions'),
            href: '/payment/transactions',
          },
          {
            name: t('header.top_menu.subscriptions'),
            href: '/payment/subscriptions',
          },
          {
            name: t('header.top_menu.affiliate_invoices'),
            href: '/payment/affiliate-invoices',

            hideForWorkspaceAssistant: true,
            hide: isClient() ? !isSioHost(window.location.host) : true,
          },
          {
            name: t('header.top_menu.affiliate_program'),
            href: '/affiliate-program',
            hideForWorkspace: true,
            hide: isClient() ? !isSioHost(window.location.host) : true,
          },
        ],
        mobileIconRenderer: () => <SalesIcon />,
      },
    },
    {
      item: {
        name: t('header.top_menu.admin.admin'),
        isAdmin: true,
        dropdownItems: [
          {
            name: t('header.top_menu.admin.users'),
            href: '/management/users',
            adminRoles: [AdminRolesEnum.support, AdminRolesEnum.admin, AdminRolesEnum.superAdmin],
          },
          {
            name: t('header.top_menu.admin.templates'),
            dropdownItems: templateItems,
          },
          {
            name: t('header.top_menu.admin.customers'),
            href: '/management/user-customer-list',
            adminRoles: [
              AdminRolesEnum.mailingManager,
              AdminRolesEnum.support,
              AdminRolesEnum.admin,
              AdminRolesEnum.superAdmin,
            ],
          },
          {
            name: t('header.top_menu.admin.emails'),
            href: '/management/emails-delivery-statistics',
            adminRoles: [AdminRolesEnum.support, AdminRolesEnum.admin, AdminRolesEnum.superAdmin],
          },
          {
            name: t('header.top_menu.admin.user_plans'),
            href: '/management/user-plans',
            adminRoles: [AdminRolesEnum.admin, AdminRolesEnum.superAdmin],
            hideForWorkspaceAssistant: true,
          },
          {
            name: t('header.top_menu.admin.domains_blocked'),
            href: '/management/blocked-mailbox-provider',
            adminRoles: [AdminRolesEnum.support, AdminRolesEnum.admin, AdminRolesEnum.superAdmin],
          },
          {
            name: t('header.top_menu.admin.domains_white_listed'),
            href: '/management/white-list-mailbox',
            adminRoles: [AdminRolesEnum.support, AdminRolesEnum.admin, AdminRolesEnum.superAdmin],
          },
          {
            name: t('header.top_menu.admin.suspicious_content'),
            dropdownItems: [
              {
                name: t('header.top_menu.admin.suspicious_page_content_keywords'),
                href: '/management/suspicious-page-content-keyword',
                adminRoles: [
                  AdminRolesEnum.support,
                  AdminRolesEnum.admin,
                  AdminRolesEnum.superAdmin,
                ],
              },
              {
                name: t('header.top_menu.admin.suspicious_email_keywords'),
                href: '/management/suspicious-email-keyword',
                adminRoles: [
                  AdminRolesEnum.support,
                  AdminRolesEnum.admin,
                  AdminRolesEnum.superAdmin,
                ],
              },
              {
                name: t('header.top_menu.admin.suspicious_email_address'),
                href: '/management/suspicious-email-address',
                adminRoles: [
                  AdminRolesEnum.support,
                  AdminRolesEnum.admin,
                  AdminRolesEnum.superAdmin,
                ],
              },
            ],
          },
          {
            name: t('header.top_menu.admin.statistics'),
            href: '/management/statistics',
            adminRoles: [AdminRolesEnum.superAdmin],
            hideForWorkspaceAssistant: true,
          },
        ],
        mobileIconRenderer: () => <ManInSuit />,
      },
    },
    {
      item: helpItem,
    },
  ]

  const getMenuItems = (userStatus: UserStatusEnum) => {
    const filterMenuItems = (items: MenuItemInterface[]) => {
      function checkItem(menuItem: SingleItemHrefInterface | SingleItemDropdownInterface) {
        if (needToHide(menuItem)) {
          return false
        }
        if ('dropdownItems' in menuItem && menuItem.dropdownItems) {
          menuItem.dropdownItems = menuItem.dropdownItems.filter(checkItem)
          if (menuItem.dropdownItems.length === 0) {
            return false
          }
        }
        return true
      }

      return items.filter(item => checkItem(item.item))
    }

    let items: MenuItemInterface[] = []

    switch (userStatus) {
      case UserStatusEnum.affiliate: {
        items = menuItemsAffiliate
        break
      }
      case UserStatusEnum.affiliate_student: {
        items = menuItemsAffiliateStudent
        break
      }
      default: {
        items = menuItems
        break
      }
    }

    return filterMenuItems(items)
  }
  return { getMenuItems, upgradeAccountItem }
}
